import React, {Component} from 'react';
import {Link} from 'react-router-dom';
import {withTranslation} from 'react-i18next';
import {FaIcon} from '../FaIcon';
import {KnewsService} from './KnewsService';
import {LocalStorage} from '../../utils/LocalStorage';
import {SupportChatService} from '../support_chat/SupportChatService';
import './Knews.scss';


// Las novedades cambian muy poco. Cacheamos el unread count 60 min en localStorage
// (sobrevive F5/navegación) para no pegarle a /knews/unread-count/ en cada focus de
// ventana, poll o remount. El poll usa el mismo período que el TTL.
const CACHE_TTL_MS = 60 * 60 * 1000;
const POLL_INTERVAL_MS = CACHE_TTL_MS;

const CHANGE_TYPE_ICON = {
    bug_fix: '🐛',
    improvement: '🛠',
    new_feature: '✨',
};

const LIFECYCLE_PRODUCTION = 'production';
const LIFECYCLE_BETA_FEEDBACK = 'beta_feedback';
const LIFECYCLE_BETA_REQUEST_ACCESS = 'beta_request_access';

function openIntercomNewMessage(message) {
    // Ruteo por SupportChatService: abre la burbuja de soporte si el user está en la allowlist,
    // o cae a Intercom (mismo guard de siempre) si no. Transparente para el call site.
    try {
        SupportChatService.showNewMessage(message);
        return true;
    } catch (_e) {
        return false;
    }
}


class KnewsBellClass extends Component {
    constructor(props) {
        super(props);
        // Arrancamos con el último count cacheado si todavía está fresco, así el bell
        // se pinta sin esperar el fetch (y no parpadea en cada navegación). Si está
        // vencido, arrancamos en 0 y el mount dispara el fetch que lo corrige.
        const cached = LocalStorage.getKnewsUnreadCount();
        const cachedIsFresh = cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS;
        this.state = {
            unreadCount: cachedIsFresh ? cached.count : 0,
            isOpen: false,
            items: [],
            loadingItems: false,
            expandedIds: new Set(),
        };
        this.handleToggle = this.handleToggle.bind(this);
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
        this.handleItemClick = this.handleItemClick.bind(this);
        this.toggleExpanded = this.toggleExpanded.bind(this);
        this.handleWindowFocus = this.handleWindowFocus.bind(this);
        this.rootRef = React.createRef();
    }

    componentDidMount() {
        if (!this.props.enabled) return;
        this.startPolling();
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.enabled && this.props.enabled) {
            this.startPolling();
        }
    }

    componentWillUnmount() {
        this.stopPolling();
    }

    // Idempotente: limpia antes de registrar para no acumular intervals/listeners si
    // `enabled` oscila (en el código previo cada re-registro dejaba un setInterval huérfano).
    startPolling() {
        this.stopPolling();
        this.fetchUnreadCount();
        this.pollTimer = setInterval(() => this.fetchUnreadCount(), POLL_INTERVAL_MS);
        window.addEventListener('focus', this.handleWindowFocus);
        document.addEventListener('mousedown', this.handleOutsideClick);
    }

    stopPolling() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
        window.removeEventListener('focus', this.handleWindowFocus);
        document.removeEventListener('mousedown', this.handleOutsideClick);
    }

    handleWindowFocus() {
        this.fetchUnreadCount();
    }

    // Respeta el caché de 60 min: si el último fetch fue hace menos de CACHE_TTL_MS,
    // refleja el valor cacheado en el state y NO pega al backend. `force` lo saltea.
    fetchUnreadCount = ({force = false} = {}) => {
        if (!this.props.enabled) return;
        const cached = LocalStorage.getKnewsUnreadCount();
        if (!force && cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS) {
            if (cached.count !== this.state.unreadCount) {
                this.setState({unreadCount: cached.count});
            }
            return;
        }
        KnewsService.getUnreadCount()
            .then((data) => {
                if (data && typeof data.count === 'number') {
                    LocalStorage.saveKnewsUnreadCount(data.count);
                    this.setState({unreadCount: data.count});
                }
            })
            .catch(() => {
                // Silencioso: si falla, simplemente no mostramos el bell
            });
    };

    fetchItems() {
        this.setState({loadingItems: true});
        KnewsService.list(1, 5)
            .then((data) => {
                const items = (data && data.knews) ? data.knews : [];
                this.setState({items, loadingItems: false});
            })
            .catch(() => {
                this.setState({loadingItems: false});
            });
    }

    handleToggle(event) {
        event.preventDefault();
        event.stopPropagation();
        const willOpen = !this.state.isOpen;
        this.setState({isOpen: willOpen});
        if (willOpen) {
            this.fetchItems();
            // "Abrir el listado" = "ver todas". Apaga el puntito rojo aunque el
            // user no clickee ítem por ítem.
            if (this.state.unreadCount > 0) {
                KnewsService.markAllSeen()
                    .then(() => {
                        LocalStorage.saveKnewsUnreadCount(0);
                        this.setState({unreadCount: 0});
                    })
                    .catch(() => {});
            }
        }
    }

    handleOutsideClick(event) {
        if (this.rootRef.current && !this.rootRef.current.contains(event.target)) {
            if (this.state.isOpen) this.setState({isOpen: false});
        }
    }

    hasCta(knew) {
        const lifecycle = knew.lifecycle_status || LIFECYCLE_PRODUCTION;
        const isBetaRequest = lifecycle === LIFECYCLE_BETA_REQUEST_ACCESS;
        return isBetaRequest || !!(knew.cta_text && knew.link_url);
    }

    toggleExpanded(id) {
        this.setState((prev) => {
            const expandedIds = new Set(prev.expandedIds);
            if (expandedIds.has(id)) {
                expandedIds.delete(id);
            } else {
                expandedIds.add(id);
            }
            return {expandedIds};
        });
    }

    handleItemClick(knew) {
        // Ítems sin CTA se expanden/colapsan al clickear para mostrar el body completo.
        if (!this.hasCta(knew)) {
            this.toggleExpanded(knew.id);
        }
        KnewsService.markSeen(knew.id)
            .then(() => {
                this.setState(
                    (s) => ({
                        unreadCount: Math.max(0, s.unreadCount - (knew.seen ? 0 : 1)),
                        items: s.items.map((c) => c.id === knew.id ? {...c, seen: true} : c),
                    }),
                    () => LocalStorage.saveKnewsUnreadCount(this.state.unreadCount),
                );
            })
            .catch(() => {});
    }

    handleCtaClick(knew, event) {
        event.stopPropagation();
        event.preventDefault();
        KnewsService.trackClick(knew.id).catch(() => {});
        KnewsService.markSeen(knew.id).catch(() => {});
        const url = knew.link_url;
        if (!url) return;
        if (/^https?:/i.test(url)) {
            window.open(url, '_blank', 'noopener,noreferrer');
        } else {
            window.location.href = url;
        }
    }

    handleRequestAccessClick(knew, event) {
        event.stopPropagation();
        event.preventDefault();
        KnewsService.trackClick(knew.id).catch(() => {});
        KnewsService.markSeen(knew.id).catch(() => {});
        const {t} = this.props;
        const prefix = t('knews.beta_request_access_intercom_prefix');
        openIntercomNewMessage(`${prefix} ${knew.title}`);
    }

    renderDot() {
        if (this.state.unreadCount <= 0) return null;
        return <span className="knews-bell-dot" aria-hidden="true"/>;
    }

    renderDropdown() {
        if (!this.state.isOpen) return null;
        const {t} = this.props;
        const {items, loadingItems, unreadCount} = this.state;

        return (
            <div className="knews-dropdown" role="dialog" aria-label={t('knews.bell_label')}>
                <div className="knews-dropdown__rail" aria-hidden="true"/>
                <div className="knews-dropdown__header">
                    <span className="knews-dropdown__kicker">{t('knews.kicker')}</span>
                    <h3 className="knews-dropdown__title">{t('knews.title')}</h3>
                    {unreadCount > 0 && (
                        <span className="knews-dropdown__count">
                            {t('knews.unread_n', {count: unreadCount})}
                        </span>
                    )}
                </div>

                <div className="knews-dropdown__list">
                    {loadingItems && (
                        <div className="knews-dropdown__empty">{t('knews.loading')}</div>
                    )}
                    {!loadingItems && items.length === 0 && (
                        <div className="knews-dropdown__empty">{t('knews.empty_short')}</div>
                    )}
                    {!loadingItems && items.map((c) => {
                        const lifecycle = c.lifecycle_status || LIFECYCLE_PRODUCTION;
                        const isBetaRequest = lifecycle === LIFECYCLE_BETA_REQUEST_ACCESS;
                        const isBetaFeedback = lifecycle === LIFECYCLE_BETA_FEEDBACK;
                        const hasCta = this.hasCta(c);
                        const isExpanded = this.state.expandedIds.has(c.id);
                        return (
                            <div
                                key={c.id}
                                onClick={() => this.handleItemClick(c)}
                                className={`knews-dropdown__item ${c.seen ? '' : '-unread'}`}
                                role="button"
                                tabIndex={0}
                            >
                                <span className="knews-dropdown__item-icon" aria-hidden="true">
                                    {CHANGE_TYPE_ICON[c.change_type] || '·'}
                                </span>
                                <span className="knews-dropdown__item-body">
                                    <span className="knews-dropdown__item-title">{c.title}</span>
                                    {(isBetaFeedback || isBetaRequest) && (
                                        <span className={`knews-lifecycle-badge ${isBetaRequest ? '-request' : '-feedback'}`}>
                                            {isBetaRequest
                                                ? t('knews.lifecycle_beta_request_access')
                                                : t('knews.lifecycle_beta_feedback')}
                                        </span>
                                    )}
                                    <span className={`knews-dropdown__item-excerpt ${isExpanded ? '-expanded' : ''}`}>{c.body}</span>
                                    {!hasCta && (
                                        <span className="knews-dropdown__item-readmore">
                                            {isExpanded ? t('knews.read_less') : t('knews.read_more')}
                                        </span>
                                    )}
                                    {isBetaRequest && (
                                        <button
                                            type="button"
                                            onClick={(e) => this.handleRequestAccessClick(c, e)}
                                            className="knews-dropdown__item-cta -request"
                                        >
                                            {t('knews.beta_request_access_cta')} <span aria-hidden="true">→</span>
                                        </button>
                                    )}
                                    {!isBetaRequest && c.cta_text && c.link_url && (
                                        <button
                                            type="button"
                                            onClick={(e) => this.handleCtaClick(c, e)}
                                            className="knews-dropdown__item-cta"
                                        >
                                            {c.cta_text} <span aria-hidden="true">→</span>
                                        </button>
                                    )}
                                </span>
                                {!c.seen && <span className="knews-dropdown__item-flag" aria-hidden="true"/>}
                            </div>
                        );
                    })}
                </div>

                <Link
                    to="/novedades"
                    className="knews-dropdown__footer"
                    onClick={() => this.setState({isOpen: false})}
                >
                    {t('knews.view_all')} <span aria-hidden="true">→</span>
                </Link>
            </div>
        );
    }

    render() {
        const {t, enabled} = this.props;

        // Bell siempre visible para SuperAdmins. El puntito rojo aparece solo
        // si hay unread (lógica en renderDot). Cuando el user abre el dropdown
        // o entra a /novedades, `mark-all-seen` apaga el contador.
        if (!enabled) return null;

        return (
            <div ref={this.rootRef} className="knews-bell-root">
                <button
                    type="button"
                    onClick={this.handleToggle}
                    className="sidebar-main-link knews-bell-trigger"
                    aria-label={t('knews.bell_label')}
                    aria-expanded={this.state.isOpen}
                >
                    <span className="knews-bell-icon-wrap">
                        <FaIcon iconName="bell"/>
                        {this.renderDot()}
                    </span>
                    <span className="tooltip-text">{t('knews.title')}</span>
                </button>
                {this.renderDropdown()}
            </div>
        );
    }
}

export const KnewsBell = withTranslation()(KnewsBellClass);
export default KnewsBell;
